/**
 * BotSpot Web Dashboard - Credential List Component
 *
 * Displays a list of credentials with management actions.
 */

import React from "react";
import type { Credential } from "../api/types";
import { YELLOW, PIXEL, MONO } from "../theme";

/**
 * Props interface
 */
interface CredentialListProps {
  credentials: Credential[];
  onEdit: (credential: Credential) => void;
  onDelete: (credentialId: string) => void;
  loading?: boolean;
}

/**
 * Credential List Component
 *
 * Displays credentials in a table format with edit and delete actions.
 */
export function CredentialList({
  credentials,
  onEdit,
  onDelete,
  loading = false,
}: CredentialListProps) {
  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /**
   * Get provider display name
   */
  const getProviderName = (provider: string): string => {
    const names: Record<string, string> = {
      claude: "Claude AI",
      openai: "OpenAI",
      discord: "Discord",
      telegram: "Telegram",
      slack: "Slack",
      github: "GitHub",
      custom: "Custom",
    };
    return names[provider] || provider;
  };

  /**
   * Get status badge
   */
  const getStatusBadge = (credential: Credential) => {
    if (credential.is_expired) {
      return (
        <span style={styles.badgeExpired}>
          ⚠️ Expired
        </span>
      );
    }
    if (!credential.is_active) {
      return (
        <span style={styles.badgeInactive}>
          ⏸️ Inactive
        </span>
      );
    }
    return (
      <span style={styles.badgeActive}>
        ✓ Active
      </span>
    );
  };

  /**
   * Handle delete with confirmation
   */
  const handleDelete = (credential: Credential) => {
    if (
      confirm(
        `Are you sure you want to delete "${credential.name}"? This action cannot be undone.`
      )
    ) {
      onDelete(credential.id);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Provider</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Last Used</th>
              <th style={styles.th}>Expires</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((credential) => (
              <tr
                key={credential.id}
                style={{
                  ...styles.tr,
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <td style={styles.td}>
                  <div style={styles.providerCell}>
                    {getProviderName(credential.provider)}
                  </div>
                </td>
                <td style={styles.td}>
                  <div style={styles.nameCell}>{credential.name}</div>
                </td>
                <td style={styles.td}>{getStatusBadge(credential)}</td>
                <td style={styles.td}>
                  <div style={styles.dateCell}>
                    {formatDate(credential.last_used_at)}
                  </div>
                </td>
                <td style={styles.td}>
                  <div style={styles.dateCell}>
                    {credential.expires_at
                      ? formatDate(credential.expires_at)
                      : "Never"}
                  </div>
                </td>
                <td style={styles.td}>
                  <div style={styles.actionCell}>
                    <button
                      style={styles.editButton}
                      onClick={() => onEdit(credential)}
                      disabled={loading}
                      title="Edit credential"
                    >
                      ✏️
                    </button>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDelete(credential)}
                      disabled={loading}
                      title="Delete credential"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {credentials.length === 0 && (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No credentials found</p>
        </div>
      )}
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  tableWrapper: {
    overflowX: "auto",
    backgroundColor: "rgba(0,106,167,0.06)",
    border: "1px solid rgba(0,106,167,0.2)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: "1rem",
    textAlign: "left",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 600,
    color: YELLOW,
    textShadow: "0 0 8px rgba(254,204,0,0.3)",
    borderBottom: "1px solid rgba(0,106,167,0.15)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  tr: {
    transition: "background-color 0.2s",
    borderBottom: "1px solid rgba(0,106,167,0.08)",
  },
  td: {
    padding: "1rem",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255, 255, 255, 0.87)",
  },
  providerCell: {
    fontFamily: MONO,
    fontWeight: 500,
  },
  nameCell: {
    fontFamily: MONO,
    fontWeight: 500,
  },
  dateCell: {
    fontFamily: MONO,
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.875rem",
  },
  badgeActive: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    backgroundColor: "rgba(0,201,122,0.15)",
    border: "1px solid rgba(0,201,122,0.3)",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 500,
    color: "#00c97a",
    letterSpacing: "0.08em",
  },
  badgeInactive: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    backgroundColor: "rgba(156, 163, 175, 0.2)",
    border: "1px solid rgba(156, 163, 175, 0.3)",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 500,
    color: "#d1d5db",
    letterSpacing: "0.08em",
  },
  badgeExpired: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 500,
    color: "#fca5a5",
    letterSpacing: "0.08em",
  },
  actionCell: {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
  },
  editButton: {
    padding: "0.5rem 0.75rem",
    backgroundColor: "rgba(0,106,167,0.15)",
    border: "1px solid rgba(0,106,167,0.3)",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "all 0.2s",
  },
  deleteButton: {
    padding: "0.5rem 0.75rem",
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "all 0.2s",
  },
  emptyState: {
    padding: "3rem",
    textAlign: "center",
  },
  emptyText: {
    fontFamily: MONO,
    margin: 0,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.5)",
  },
};
